/**
 * 合併站牌關聯路線（與 backend/utils/bus-stop-routes-merge.js 保持同步）
 * 紅線：站組＝所在縣｜StationID；禁裸數字；跨距過大不成組；合併不按營運縣過濾。
 */
import { buildRouteFamilyId } from './bus-route-taxonomy.js';
import { normalizeBusCityCode } from './bus-route-display.js';

const ROLE_RANK = Object.freeze({ display: 0, primary: 1, variant: 2 });
export const STATION_GROUP_MAX_SPAN_M = 100;

export function cityCodeFromStopId(stopId) {
    const m = String(stopId || '').match(/^([A-Za-z]{3})/);
    return m ? normalizeBusCityCode(m[1]) : '';
}

export function stopOperatorCityCode(stopProps = {}) {
    const fromProp = stopProps.h_city_code || stopProps.city_code;
    if (fromProp) return normalizeBusCityCode(fromProp);
    return cityCodeFromStopId(stopProps.stop_id || stopProps.id);
}

export function stopLocationCityCode(stopProps = {}) {
    const fromLoc =
        stopProps.h_location_city_code ||
        stopProps.location_city_code ||
        stopProps.LocationCityCode;
    if (fromLoc) return normalizeBusCityCode(fromLoc);
    return stopOperatorCityCode(stopProps);
}

/** @deprecated 相容：站組語意＝所在縣 */
export function stopCityCode(stopProps = {}) {
    return stopLocationCityCode(stopProps);
}

export function stationGroupKey(stopProps = {}) {
    const baked = stopProps.h_station_key;
    if (baked != null && String(baked).includes('|')) return String(baked);
    const stationId = stopProps.h_ref_codes?.tdx_station_id || stopProps.station_id;
    if (stationId == null || String(stationId).trim() === '') return null;
    const city = stopLocationCityCode(stopProps);
    return city ? `${city}|${String(stationId).trim()}` : null;
}

export function filterStopIdsToCity(stopIds, city) {
    if (!city) return [...(stopIds || [])];
    return (stopIds || []).filter((id) => {
        const c = cityCodeFromStopId(id);
        return !c || c === city;
    });
}

export function filterRoutesByCity(routes, city) {
    if (!city) return routes || [];
    return (routes || []).filter((r) => {
        const rc = normalizeBusCityCode(r?.h_city_code || r?.city_code || '');
        if (rc) return rc === city;
        const fromUid = cityCodeFromStopId(r?.route_id || r?.tdx_route_uid || '');
        return !fromUid || fromUid === city;
    });
}

function haversineM(a, b) {
    const R = 6371000;
    const toR = (d) => (d * Math.PI) / 180;
    const dLat = toR(b[1] - a[1]);
    const dLon = toR(b[0] - a[0]);
    const la1 = toR(a[1]);
    const la2 = toR(b[1]);
    const h =
        Math.sin(dLat / 2) ** 2 +
        Math.cos(la1) * Math.cos(la2) * Math.sin(dLon / 2) ** 2;
    return 2 * R * Math.asin(Math.sqrt(h));
}

function maxSpanM(coords) {
    let max = 0;
    for (let i = 0; i < coords.length; i++) {
        for (let j = i + 1; j < coords.length; j++) {
            max = Math.max(max, haversineM(coords[i], coords[j]));
        }
    }
    return max;
}

export function buildStationStopGroups(stopsFc, opts = {}) {
    const maxSpan = opts.maxSpanM ?? STATION_GROUP_MAX_SPAN_M;
    const byStation = new Map();
    for (const f of stopsFc?.features || []) {
        const p = f.properties || {};
        const sid = p.stop_id || p.id;
        const stationId = p.h_ref_codes?.tdx_station_id || p.station_id;
        if (!sid || stationId == null || String(stationId).trim() === '') continue;
        const key = stationGroupKey(p);
        if (!key) continue;
        if (!byStation.has(key)) byStation.set(key, new Map());
        const coords = f.geometry?.coordinates;
        byStation.get(key).set(String(sid), Array.isArray(coords) ? coords : null);
    }
    const out = new Map();
    for (const [stationKey, memberMap] of byStation) {
        if (memberMap.size < 2) continue;
        const coords = [...memberMap.values()].filter(
            (c) => Array.isArray(c) && c.length >= 2
        );
        if (coords.length >= 2 && maxSpanM(coords) > maxSpan) continue;
        out.set(stationKey, [...memberMap.keys()]);
    }
    return out;
}

export function attachStationStopIds(stopProps, stationGroups) {
    if (!stopProps || !stationGroups?.size) return stopProps;
    const stationId = stopProps.h_ref_codes?.tdx_station_id || stopProps.station_id;
    if (stationId == null) return stopProps;
    const scopedKey = stationGroupKey(stopProps);
    const members = (scopedKey && stationGroups.get(scopedKey)) || [];
    if (members.length) stopProps.stop_ids = members;
    return stopProps;
}

export function collectStopKeys(stopProps = {}) {
    const keys = new Set();
    const main = stopProps.stop_id || stopProps.id;
    if (main) keys.add(String(main));
    for (const id of stopProps.stop_ids || []) {
        if (id != null && String(id).trim()) keys.add(String(id));
    }
    return [...keys];
}

export function routeDirectionFromId(routeId) {
    const m = String(routeId ?? '').match(/_D(\d+)$/i);
    if (m) return m[1];
    return '0';
}

export function routeFamilyKey(detail = {}) {
    if (detail.h_route_family_id) return String(detail.h_route_family_id);
    const uid = detail.tdx_route_uid || detail.h_ref_codes?.tdx_route_uid || null;
    if (uid) return buildRouteFamilyId(uid);
    const ref = String(detail.ref || detail.name || '').trim();
    const dir = routeDirectionFromId(detail.route_id);
    return ref ? `REF:${ref}|D${dir}` : String(detail.route_id || '');
}

export function routeDisplayDedupeKey(detail = {}) {
    const family = routeFamilyKey(detail);
    const dir = routeDirectionFromId(detail.route_id);
    return `${family}|D${dir}`;
}

export function dedupeRouteDetailsForDisplay(routes) {
    const byKey = new Map();
    for (const r of routes || []) {
        if (!r) continue;
        const key = routeDisplayDedupeKey(r);
        if (!key) continue;
        const existing = byKey.get(key);
        if (!existing) {
            byKey.set(key, r);
            continue;
        }
        const existingRank = ROLE_RANK[existing.h_route_role] ?? 1;
        const newRank = ROLE_RANK[r.h_route_role] ?? 1;
        if (newRank < existingRank) byKey.set(key, r);
    }
    return [...byKey.values()].sort((a, b) =>
        String(a.ref || a.name || '').localeCompare(String(b.ref || b.name || ''), 'zh-Hant')
    );
}

export function mergeRoutesForStop(stopToRoutes, stopProps, stationToRoutes = null) {
    const scopedStationKey = stationGroupKey(stopProps);

    if (scopedStationKey && stationToRoutes?.[scopedStationKey]?.length) {
        return dedupeRouteDetailsForDisplay(stationToRoutes[scopedStationKey]);
    }

    const collected = [];
    for (const sid of collectStopKeys(stopProps)) {
        for (const r of stopToRoutes?.[sid] || []) {
            if (r) collected.push(r);
        }
    }
    return dedupeRouteDetailsForDisplay(collected);
}
